import React, { useState } from 'react'
import { clsx } from 'clsx'
import { Area } from 'types/area'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Transition } from '@headlessui/react'

type BaseRadio = React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>

export type InputRadioExpandableProps = Omit<BaseRadio, 'type' | 'checked' | 'onChange'> & {
  value: string
  selectedValue: string | undefined
  onChange: (value: string) => void
  label?: string
  childrens?: Area[] // for recursive children
  level?: number
  disabled?: boolean
}

export const InputRadioExpandable = ({
  value,
  selectedValue,
  onChange,
  label,
  childrens,
  level = 0,
  disabled,
  ...rest
}: InputRadioExpandableProps) => {
  const [expanded, setExpanded] = useState(false)
  const hasChildren = childrens && childrens.length > 0
  const isSelected = selectedValue === value

  const toggleExpand = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (hasChildren) {
      setExpanded(!expanded)
    }
  }

  const onChangeHandler = (value: string) => {
    onChange(value)
  }

  return (
    <div style={{ marginLeft: `${level * 8}px` }}>
      <div className={clsx('tw-flex tw-items-center tw-text-sm tw-mb-2')}>
        <FontAwesomeIcon
          onClick={toggleExpand}
          className={clsx(
            'tw-transition tw-ml-0.5',
            expanded ? 'tw-transform tw-rotate-90' : '',
            !hasChildren && 'tw-opacity-0',
            hasChildren && 'tw-cursor-pointer',
          )}
          icon={['far', 'chevron-right']}
        />
        <div className={'tw-flex tw-items-center tw-flex-grow tw-ml-2'}>
          <input
            type="radio"
            className="tw-sr-only"
            checked={isSelected}
            onChange={() => !disabled && onChangeHandler(value)}
            {...rest}
          />
          <div
            className={clsx(
              'tw-w-5 tw-h-5 tw-rounded-full tw-border tw-border-input tw-mr-2 tw-shrink-0',
              'tw-flex tw-items-center tw-justify-center',
              'tw-transition-colors tw-duration-200',
              !isSelected && 'tw-bg-input-fill',
              disabled ? 'tw-cursor-not-allowed tw-opacity-50' : 'tw-cursor-pointer',
            )}
            style={
              isSelected ? { backgroundColor: 'var(--primary-500)', borderColor: 'var(--primary-500)' } : undefined
            }
            onClick={() => !disabled && onChangeHandler(value)}
          >
            {isSelected && <div className="tw-w-2.5 tw-h-2.5 tw-rounded-full tw-bg-surface" />}
          </div>
          {label && (
            <label
              className={clsx(
                'tw-text-input tw-flex-1 tw-truncate',
                disabled ? 'tw-cursor-not-allowed' : 'tw-cursor-pointer',
                isSelected && 'tw-font-semibold',
              )}
              onClick={() => !disabled && onChangeHandler(value)}
            >
              {label}
            </label>
          )}
        </div>
      </div>
      {hasChildren && (
        <Transition
          show={expanded}
          enter="tw-transition-opacity tw-duration-200"
          enterFrom="tw-opacity-0"
          enterTo="tw-opacity-100"
          leave="tw-transition-opacity tw-duration-50"
          leaveFrom="tw-opacity-100"
          leaveTo="tw-opacity-0"
          className={'tw-ml-4'}
        >
          {childrens.map((child) => (
            <InputRadioExpandable
              key={child._id}
              value={child._id}
              label={child.name}
              disabled={child.numberOfAreaSources === 0}
              childrens={child.children}
              selectedValue={selectedValue}
              onChange={onChangeHandler}
              level={level + 1}
            />
          ))}
        </Transition>
      )}
    </div>
  )
}
